import React, { useEffect, useState } from 'react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { C4DiagramCard } from '../C4DiagramCard/C4DiagramCard';
import { Checkbox, FormControlLabel, Grid, TextField } from '@material-ui/core';
import { C4DiagramModel, toC4DiagramEntity } from '../C4DiagramCard/types';
// @ts-ignore
import * as uuid from 'uuid';
import Autocomplete from '@material-ui/lab/Autocomplete';

type OutputState = {
  entities: Entity[];
};

export const SystemLandscapeCard = () => {
  const catalogApi = useApi(catalogApiRef);
  const [hideExternal, setHideExternal] = useState(true);
  const [hideDomainExternal, setHideDomainExternal] = useState(true);
  const [hideRelationless, setHideRelationless] = useState(true);
  const [domain, setDomain] = useState<string | null>('*');
  const [outputState, setOutputState] = useState<OutputState>(() => {
    return {
      entities: [],
    };
  });

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: {
          kind: 'system',
        },
      })
      .then(response => {
        setOutputState({
          entities: response.items,
        });
      });
  }, [catalogApi]);

  if (outputState.entities.length > 0) {
    const model: C4DiagramModel = {
      type: 'landscape',
      entities: [],
      relations: [],
    };
    const externalSystems = outputState.entities.filter(it =>
      it.metadata.tags?.includes('external'),
    );
    const domainSystems = outputState.entities
      .filter(it => !it.metadata.tags?.includes('external'))
      .filter(
        it =>
          domain === '*' ||
          domain === '' ||
          (domain && it.metadata.tags?.includes(domain.toLowerCase())),
      );
    const visibleEntities: Entity[] = [];

    if (!hideExternal) {
      externalSystems.forEach(system => visibleEntities.push(system));
    }

    domainSystems.forEach(system => {
      visibleEntities.push(system);
      if (!hideDomainExternal) {
        system.relations
          ?.filter(it => it.type === 'dependsOn' || it.type === 'dependencyOf')
          .map(it => parseEntityRef(it.targetRef))
          .filter(it => it.kind === 'system')
          .forEach(dependency => {
            const dependencyEntity = outputState.entities.find(
              it => it.metadata.name === dependency.name,
            )!;
            if (
              !visibleEntities.includes(dependencyEntity) &&
              !dependencyEntity.metadata.tags?.includes('external')
            ) {
              visibleEntities.push(dependencyEntity);
            }
          });
      }
    });

    const visibleEntitiesNames = visibleEntities.map(it => it.metadata.name);
    const alreadyDefinedReleations: string[] = [];
    visibleEntities.forEach(entity => {
      const dependsOnSystems = entity.relations
        ?.filter(it => it.type === 'dependsOn')
        .map(it => parseEntityRef(it.targetRef))
        .filter(it => it.kind === 'system')
        .map(it => it.name)
        .filter(it => visibleEntitiesNames.includes(it));
      const dependencyOfSystems = entity.relations
        ?.filter(it => it.type === 'dependencyOf')
        .map(it => parseEntityRef(it.targetRef))
        .filter(it => it.kind === 'system')
        .map(it => it.name)
        .filter(it => visibleEntitiesNames.includes(it));
      const hasRelations =
        (dependsOnSystems && dependsOnSystems.length > 0) ||
        (dependencyOfSystems && dependencyOfSystems.length > 0);
      if (
        (!hideRelationless || hasRelations) &&
        (!entity.metadata.tags?.includes('external') || hasRelations)
      ) {
        model.entities.push(
          toC4DiagramEntity(entity, 'context', domainSystems.includes(entity)),
        );
      }
      if (dependsOnSystems) {
        for (const system of dependsOnSystems) {
          const relationName = `${entity.metadata.name} -> ${system}`;
          if (!alreadyDefinedReleations.includes(relationName)) {
            model.relations.push({
              id: uuid.v4(),
              source: entity.metadata.name,
              target: system,
            });
            alreadyDefinedReleations.push(relationName);
          }
        }
      }
    });
    return (
      <>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={4} lg={3} xl={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideExternal}
                  onChange={() => setHideExternal(!hideExternal)}
                />
              }
              label="Hide external systems"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} xl={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideDomainExternal}
                  onChange={() => setHideDomainExternal(!hideDomainExternal)}
                />
              }
              label="Hide domain external systems"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} xl={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideRelationless}
                  onChange={() => setHideRelationless(!hideRelationless)}
                />
              }
              label="Hide systems without relations"
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} xl={2}>
            <Autocomplete
              options={[
                '*',
                'Advertising-Intelligence',
                'Operational-Intelligence',
              ]}
              value={domain}
              freeSolo
              renderInput={params => (
                <TextField
                  {...params}
                  margin="dense"
                  variant="outlined"
                  InputProps={params.InputProps}
                />
              )}
              onChange={(_, newValue) => {
                setDomain(newValue);
                setHideExternal(newValue === '*');
                setHideDomainExternal(newValue === '*');
                setHideRelationless(newValue === '*');
              }}
            />
          </Grid>
        </Grid>
        <C4DiagramCard model={model} />
      </>
    );
  }
  return <></>;
};
